import { Button } from "@/modules/shadcn/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import {
  collectCustomHyperparamIssues,
  getHyperparamRegistryEntry,
  ModelBackendEnum,
  ProjectTypeEnum,
  type SuggestibleParamDef,
} from "@repo/schema";
import { createZodLinter } from "./zodLinter";
import type { TFunction } from "i18next";
import { RefreshCw, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// import { toast } from "sonner";
import { getHyperparamsPresets, type HyperparamsPreset } from "../presets";
// Reserved keys imports kept for reference — stripping intentionally bypassed
// import {
//   RESERVED_HYPERPARAMS_PATHS,
//   createReservedKeysLinter,
//   stripReservedKeys,
// } from "./reservedKeys";
import { useCodeMirror } from "./useCodeMirror";

export interface HyperparamsModalProps {
  backend: ModelBackendEnum;
  trainingType: ProjectTypeEnum;
  value: string;
  onApply: (value: string) => void;
  onClose: () => void;
}

/** The allowed values of a registry param, as shown in the key reference. */
const describeRange = (def: SuggestibleParamDef): string | null => {
  if (def.input === "enum") return def.options?.join(" | ") ?? null;
  if (def.input === "boolean") return "true | false";
  if (def.min !== undefined && def.max !== undefined) {
    return `${def.min} – ${def.max}`;
  }
  if (def.min !== undefined) return `≥ ${def.min}`;
  if (def.max !== undefined) return `≤ ${def.max}`;
  return null;
};

type ModelT = TFunction<["model", "common"]>;

function validateJson(
  text: string,
  backend: ModelBackendEnum,
  trainingType: ProjectTypeEnum,
  t: ModelT,
): string[] {
  const trimmed = text.trim();
  if (!trimmed || trimmed === "{}" || trimmed === "{\n  \n}") return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return [t("model:hyperparams.errors.invalidSyntax")];
  }

  if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
    return [t("model:hyperparams.errors.notObject")];
  }

  // Only values the registry knows about are checked. Other keys (`seed`,
  // `cache`, ...) pass straight through to the trainer, which drops the ones
  // it doesn't know. The API applies the same check on create, so catching it
  // here just avoids a round-trip.
  return collectCustomHyperparamIssues(
    backend,
    trainingType,
    parsed as Record<string, unknown>,
  ).map((issue) => `${issue.key}: ${issue.message}`);
}

export const HyperparamsModal = ({
  backend,
  trainingType,
  value,
  onApply,
  onClose,
}: HyperparamsModalProps) => {
  const { t } = useTranslation(["model", "common"]);
  const [editorValue, setEditorValue] = useState(value.trim() || "{\n  \n}");
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [pendingPreset, setPendingPreset] = useState<HyperparamsPreset | null>(
    null,
  );
  const presets = getHyperparamsPresets(trainingType);
  const registryEntry = getHyperparamRegistryEntry(backend, trainingType);
  // Reserved paths UI hidden — reserved keys bypass intentionally disabled
  // const [showReservedPaths, setShowReservedPaths] = useState(false);

  // Underlines the offending value in the editor. paramsSchema is a plain
  // z.object, so keys it doesn't define are stripped rather than reported —
  // which is what we want for hand-written keys like `seed`.
  const zodLinterExtension = useMemo(() => {
    const schema = registryEntry?.paramsSchema;
    return schema ? [createZodLinter(schema, t)] : [];
  }, [registryEntry, t]);

  const { containerRef, setValue } = useCodeMirror({
    initialValue: editorValue,
    onChange: (val) => {
      setEditorValue(val);
    },
    extensions: zodLinterExtension,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setErrors(validateJson(editorValue, backend, trainingType, t));
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [editorValue, backend, trainingType, t]);

  const applyPreset = (preset: HyperparamsPreset) => {
    const json = JSON.stringify(preset.config, null, 2);
    setValue(json);
    setEditorValue(json);
    setSelectedPreset(preset.id);
    setErrors([]);
    setPendingPreset(null);
  };

  const handlePresetChange = (presetId: string | null) => {
    if (!presetId) return;

    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;

    const currentTrimmed = editorValue.trim();
    const isEmpty =
      !currentTrimmed ||
      currentTrimmed === "{}" ||
      currentTrimmed === "{\n  \n}";

    // Throwing away hand-written JSON is the only destructive thing this
    // modal does, so it goes through a confirm step of its own rather than
    // the browser's.
    if (!isEmpty) {
      setPendingPreset(preset);
      return;
    }

    applyPreset(preset);
  };

  // Escape dismisses the confirm rather than the modal behind it; capture
  // phase so the editor and the Select never see the key first.
  useEffect(() => {
    if (!pendingPreset) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setPendingPreset(null);
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [pendingPreset]);

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(editorValue);
      const formatted = JSON.stringify(parsed, null, 2);
      setValue(formatted);
      setEditorValue(formatted);
    } catch {
      setErrors([t("hyperparams.errors.cannotFormat")]);
    }
  };

  const handleApply = () => {
    const trimmed = editorValue.trim();
    if (!trimmed || trimmed === "{\n  \n}") {
      onApply("");
      return;
    }

    const applyErrors = validateJson(trimmed, backend, trainingType, t);
    if (applyErrors.length > 0) {
      setErrors(applyErrors);
      return;
    }

    const parsed = JSON.parse(trimmed);
    // Reserved keys stripping intentionally bypassed — all keys pass through
    // const { cleaned, removed } = stripReservedKeys(parsed);
    //
    // if (removed.length > 0) {
    //   const labels = removed.map(
    //     (path) => `${path}: ${RESERVED_HYPERPARAMS_PATHS[path]}`,
    //   );
    //   toast.warning("Some settings were ignored", {
    //     description: labels.join("\n"),
    //     duration: 8000,
    //   });
    // }
    //
    // const isEmpty = Object.keys(cleaned).length === 0;
    // const formatted = isEmpty ? "" : JSON.stringify(cleaned, null, 2);

    const formatted = JSON.stringify(parsed, null, 2);
    onApply(formatted);
  };

  const selectedPresetDef = presets.find(
    (p) => p.id === selectedPreset,
  );

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-40 flex max-h-[85vh] w-[700px] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl bg-white shadow-[0_24px_48px_rgba(0,0,0,0.15)]">
        <div className="flex shrink-0 items-center justify-between border-b border-black/[0.08] px-6 py-4">
          <span className="text-base font-semibold">
            {t("fields.customHyperparams")}
          </span>
          <button
            type="button"
            className="flex cursor-pointer items-center border-none bg-none p-1 text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="size-4" />
          </button>
        </div>

        {/* min-h-0 is load-bearing: a flex child defaults to min-height:auto,
            which stops this from shrinking below its content, so overflow-y
            never engages and the tail of the panel becomes unreachable. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">{t("hyperparams.preset")}</span>
            <Select value={selectedPreset} onValueChange={handlePresetChange}>
              <SelectTrigger className="min-w-[220px]">
                <SelectValue placeholder={t("hyperparams.selectPreset")} />
              </SelectTrigger>
              <SelectContent>
                {presets.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {t(preset.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedPresetDef && (
              <span className="text-xs italic text-muted-foreground">
                {t(selectedPresetDef.description)}
              </span>
            )}
          </div>

          {/* shrink-0 so the editor keeps its height and the panel scrolls
              around it, rather than the editor being squeezed flat. */}
          <div
            ref={containerRef}
            className="shrink-0 overflow-hidden rounded-md border border-black/20 [&_.cm-editor]:h-[400px] [&_.cm-editor]:max-h-[50vh] [&_.cm-scroller]:overflow-auto"
          />

          {errors.length > 0 && (
            <ul className="m-0 list-disc pl-4 text-xs text-red-500 [&_li]:mb-1">
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          )}

          <span className="text-xs text-muted-foreground">
            {t("hyperparams.help")}
          </span>

          {registryEntry && (
            <details className="rounded-md border border-black/[0.08] bg-black/[0.03] text-xs">
              <summary className="cursor-pointer px-3 py-2 font-medium text-black/80">
                {t("hyperparams.reference.title")}
              </summary>
              <ul className="m-0 flex max-h-[240px] list-none flex-col gap-2 overflow-y-auto p-3 pt-1 [&_code]:rounded-[3px] [&_code]:bg-black/[0.06] [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-emerald-700">
                {Object.entries(registryEntry.params)
                  .filter(([, def]) => def.suggestible !== false)
                  .map(([key, def]) => {
                    const range = describeRange(def);
                    return (
                      <li key={key} className="leading-snug text-muted-foreground">
                        <code>{key}</code>
                        {range && <code className="ml-1">{range}</code>}
                        <span className="block">
                          {def.label}. {def.description}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            </details>
          )}

          {/* Reserved paths UI hidden — reserved keys bypass intentionally disabled
          {showReservedPaths && (
            <div className="flex max-h-[200px] flex-col gap-2 overflow-y-auto rounded-md border border-black/[0.08] bg-black/[0.03] p-3">
              <span className="text-xs font-semibold">Reserved paths</span>
              <ul className="m-0 flex list-none flex-col gap-1 p-0 [&_code]:rounded-[3px] [&_code]:bg-black/[0.06] [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-[11px] [&_code]:text-emerald-700 [&_li]:text-[11px] [&_li]:leading-snug [&_li]:text-muted-foreground">
                {Object.entries(RESERVED_HYPERPARAMS_PATHS).map(
                  ([path, reason]) => (
                    <li key={path}>
                      <code>{path}</code>
                      <span> - {reason}</span>
                    </li>
                  ),
                )}
              </ul>
            </div>
          )} */}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-black/[0.08] px-6 py-4">
          <Button variant="outline" onClick={handleFormat}>
            {t("hyperparams.formatJson")}
          </Button>
          <Button variant="outline" onClick={onClose}>
            {t("actions.goBack")}
          </Button>
          <Button onClick={handleApply}>{t("common:actions.apply")}</Button>
        </div>

        {pendingPreset && (
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="hyperparams-replace-title"
            aria-describedby="hyperparams-replace-description"
            className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-[rgba(15,15,24,0.55)] p-6"
          >
            <div className="flex w-full max-w-[420px] flex-col gap-5 rounded-xl bg-white p-6 shadow-[0_12px_32px_rgba(0,0,0,0.24)]">
              <div className="flex items-start gap-4">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-emerald-500/10 text-emerald-700">
                  <RefreshCw className="size-5" />
                </span>
                <div className="flex flex-col gap-1.5">
                  <span
                    id="hyperparams-replace-title"
                    className="text-base font-semibold leading-6 text-black/90"
                  >
                    {t("hyperparams.replace.title", {
                      name: t(pendingPreset.name),
                    })}
                  </span>
                  <span
                    id="hyperparams-replace-description"
                    className="text-sm leading-5 text-pretty text-black/60"
                  >
                    {t("hyperparams.replace.description")}
                  </span>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setPendingPreset(null)}>
                  {t("common:actions.cancel")}
                </Button>
                <Button autoFocus onClick={() => applyPreset(pendingPreset)}>
                  {t("hyperparams.replace.confirm")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
